import type { QuizCreateSchema } from '@repo/schemas/quiz/quizBuilder';

import { useErrorMessage } from '@repo/api/error-message/useErrorMessage';
import { useCreateQuiz } from '@repo/api/quiz/useCreateQuiz';
import { useEditQuizMemberChanges } from '@repo/api/quiz/useEditQuizMemberChanges';
import { quizErrorMessages } from '@repo/lib/messages/quizErrorMessages';
import { getQuestionDefaultValues } from '@repo/lib/quiz/questions/getQuestionDefaultValue';
import { QuestionType } from '@repo/types/enums/questionType';
import { QuizType } from '@repo/types/enums/quizType';
import { isAxiosError } from 'axios';
import { useNavigate } from 'react-router';
import { v4 as uuidv4 } from 'uuid';

import { toasterMessageHandler } from '@/lib/toaster-message-handler';

export function useQuizInitialization() {
  const navigate = useNavigate();
  const createQuizMutation = useCreateQuiz();
  const editQuizMemberChangesMutation = useEditQuizMemberChanges();

  const initializeQuiz = async (initialValues?: Partial<QuizCreateSchema>, members?: { userIds?: number[]; groupIds?: number[] }) => {
    try {
      // Create a minimal quiz with default values
      const response = await createQuizMutation.mutateAsync({
        name: 'Nowy quiz',
        desc: null,
        type: QuizType.QUIZ,
        maxAttempts: 0,
        time: 0,
        deadline: null,
        available: null,
        freeNavigation: false,
        instantMark: false,
        content: [
          {
            id: uuidv4(),
            name: 'Sekcja 1',
            content: 'Opis sekcji 1',
            questions: [
              {
                ...getQuestionDefaultValues(QuestionType.SINGLE),
                content: 'Opis pytania',
              },
            ],
          },
        ],
        ...initialValues,
        gradeMapping: initialValues?.gradeMapping ?? null,
      });

      if (members?.userIds?.length || members?.groupIds?.length) {
        await editQuizMemberChangesMutation.mutateAsync({
          id: response.id,
          memberChanges: [
            {
              userIds: members.userIds,
              groupIds: members.groupIds,
              add: true,
              remove: false,
            },
          ],
        });
      }

      // Navigate to the quiz builder with the new quiz ID
      navigate(`/manage/quizzes/builder/${response.id}/basic-info`, {
        replace: true,
      });

      return response.id;
    } catch (error) {
      if (isAxiosError(error)) {
        toasterMessageHandler(quizErrorMessages, error);
      }
      throw error;
    }
  };

  const errorMessage = useErrorMessage(createQuizMutation.error, quizErrorMessages);

  return {
    initializeQuiz,
    isSuccess: createQuizMutation.isSuccess,
    isPending: createQuizMutation.isPending,
    isError: createQuizMutation.isError,
    errorMessage,
  };
}
